import { getFullName } from '@/models/user/user';
import { QueryClient } from '@tanstack/react-query';
import { StoreApi } from 'zustand';

import { APPLICANT_MODE_WORKSPACE_ID } from '@/features/user-settings/types/workspace-types';

import { ISUserDetails } from '@/shared/services/user/user.types';
import { identifyUserAndSetEmailOnMixpanel } from '@/shared/utils/mixpanel-utils';
import { tabStorage } from '@/shared/utils/tab-storage';

import { AppWrapperStoreState } from './stores/app-wrapper-store';

export type OnboardingState = {
	/** Nothing left to do — enter the app / normal agent. */
	isComplete: boolean;
	/** No workspace but has applications: a job-seeker, not a hirer. Never onboards. */
	isApplicant: boolean;
	/** Backend couldn't detect a name — the one visible onboarding step. */
	needsName: boolean;
	/** Brand-new hirer (no workspace, no applications) — provision one in the background. */
	needsWorkspace: boolean;
};

/**
 * The single onboarding-state model, derived once from userDetails. Every consumer
 * (auth redirect, AppContent guard, dashboard layout, the `/ai` gate) reads from
 * this instead of re-deriving from the same three fields — so the predicates never drift.
 *
 * `isComplete` is STRUCTURAL — "can the user actually use the app": they have a name and a
 * workspace (or are an applicant). It intentionally does NOT depend on the backend
 * `showOnboarding` chat flag: that flag stays true through the agentic opener and would
 * otherwise trap the user on /ai (unable to navigate) mid-chat. `showOnboarding`
 * only governs the opener's copy (server-side); structural completeness governs the guard.
 */
export const getOnboardingState = (userDetails: ISUserDetails | null): OnboardingState => {
	if (!userDetails) {
		return { isComplete: false, isApplicant: false, needsName: false, needsWorkspace: false };
	}

	const hasWorkspaces = (userDetails.pages || []).length > 0;
	const hasApplications = userDetails.hasSubmissions;

	const needsName = !userDetails.firstName;
	const isApplicant = !hasWorkspaces && hasApplications;
	const needsWorkspace = !hasWorkspaces && !hasApplications;

	// Complete once nothing structural remains — a workspace exists (so the app is usable)
	// and a name is set. Applicants are never trapped in the hirer onboarding.
	const isComplete = isApplicant || !(needsName || needsWorkspace);

	return { isComplete, isApplicant, needsName, needsWorkspace };
};

/** Canonical completeness predicate — thin accessor over {@link getOnboardingState}. */
export const isOnboardingComplete = (userDetails: ISUserDetails | null): boolean =>
	getOnboardingState(userDetails).isComplete;

// Handle successful user details fetch
export const handleUserDetailsSuccess = (
	userDetailsData: ISUserDetails,
	store: StoreApi<AppWrapperStoreState>
) => {
	// Set user details and workspaces
	store.getState().setUserDetails(userDetailsData);

	// Set loading states
	store.getState().setIsAppLoading(false);
	store.getState().setRefetching(false);

	// Update mixpanel
	identifyUserAndSetEmailOnMixpanel({
		email: userDetailsData.email,
		name: getFullName(userDetailsData),
	});

	// Handle applicant mode switch if needed
	if (getOnboardingState(userDetailsData).isApplicant) {
		handleApplicantModeSwitch(store);
	}
};

// Handle applicant mode switching
const handleApplicantModeSwitch = (store: StoreApi<AppWrapperStoreState>) => {
	const selectedWorkspaceId = store.getState().selectedWorkspaceId;

	if (selectedWorkspaceId !== APPLICANT_MODE_WORKSPACE_ID) {
		store.getState().setSelectedWorkspaceId(APPLICANT_MODE_WORKSPACE_ID);
		tabStorage.set('selectedWorkspaceId', APPLICANT_MODE_WORKSPACE_ID);
		tabStorage.set('isFreshLogin', false);
	}
};

// Handle workspace update success
export const handleWorkspaceUpdateSuccess = (
	variables: { page_id: string },
	queryClient: QueryClient,
	store: StoreApi<AppWrapperStoreState>
) => {
	// Update the query cache optimistically
	queryClient.setQueryData(['GET_USER_DETAILS'], (oldData: ISUserDetails) => {
		if (!oldData) return oldData;
		return {
			...oldData,
			pages: oldData.pages.map((page) =>
				page.id === variables.page_id
					? { ...page, isDefaultPage: true }
					: { ...page, isDefaultPage: false }
			),
		};
	});

	// Update store
	const currentUserDetails = store.getState().userDetails;
	if (currentUserDetails) {
		const updatedUserDetails = {
			...currentUserDetails,
			pages: currentUserDetails.pages.map((page) =>
				page.id === variables.page_id
					? { ...page, isDefaultPage: true }
					: { ...page, isDefaultPage: false }
			),
		};
		store.getState().setUserDetails(updatedUserDetails);
	}
};
